import { AxiosResponse } from 'axios';

import { ISelectOption } from '~/shared/components/Form/Select/types';

import { formatCompaniesFiltersToParams } from '~/shared/services/api/utils/formatCompaniesFiltersToParams';

import { ISubscriberSubscriptionOccurrenceParameter } from '~/modules/subscribers/interfaces/ISubscriberSubscriptionOccurrence';
import { TSubscriberSubscriptionType } from '~/modules/subscribers/interfaces/ISubscriberSubscriptionContract';
import { ILoadCompanyConsumptionByIdFilters } from './interfaces/ILoadCompanyConsumptionByIdFilters';
import { ILoadCompaniesFilters } from './interfaces/ILoadCompaniesFilters';

import { ValidateOriginCnpjResponseDTO } from './dtos/ValidateOriginCnpjResponseDTO';
import { ValidateOriginCnpjRequestDTO } from './dtos/ValidateOriginCnpjRequestDTO';
import { PaginationResponseDTO } from './dtos/PaginationResponseDTO';
import { CreateCompanySubscriptionRequestDTO } from './dtos/CreateCompanySubscriptionRequestDTO';
import {
  CompanySubscriptionAmendmentValidationResponse,
  CreateCompanySubscriptionAmendmentOptions,
  CreateCompanySubscriptionAmendmentRequestDTO,
} from './dtos/CreateCompanySubscriptionAmendmentRequestDTO';
import { CompanySubscriptionsDTO } from './dtos/CompanySubscriptionDTO';
import { CompanySubscriptionAmendmentDTO } from './dtos/CompanySubscriptionAmendmentDTO';
import { CompanyResponseDTO } from './dtos/CompanyResponseDTO';
import { CompanyRequestDTO } from './dtos/CompanyRequestDTO';
import { CompanyConsumptionDTO } from './dtos/CompanyConsumptionDTO';
import { CnpjChangeOriginContractDTO } from './dtos/CnpjChangeOriginContractDTO';
import { AddActionRequestDTO } from './dtos/AddActionRequestDTO';

import { setupApi } from '.';

export async function loadCompanies(
  filters = {} as ILoadCompaniesFilters,
  ctx = undefined
): Promise<PaginationResponseDTO<CompanyResponseDTO[]>> {
  const params = formatCompaniesFiltersToParams({ ...filters, limit: 20 });

  const response = await setupApi(ctx).get<
    PaginationResponseDTO<CompanyResponseDTO[]>
  >('/companies', { params });

  return response.data;
}

export async function loadCompanyById(
  id: string,
  ctx = undefined
): Promise<CompanyResponseDTO> {
  const response = await setupApi(ctx).get<CompanyResponseDTO>(
    `/companies/${id}`
  );

  return response.data;
}

export async function loadCompanyLasOrderDateById(
  id: string
): Promise<Date | undefined> {
  const response = await setupApi().get(`/companies/${id}/last-order`);

  return !!response.data?.createdAt && new Date(response.data.createdAt);
}

export async function loadCompanyBilling(id: string): Promise<number> {
  const response = await setupApi().get(`/companies/${id}/billing`);

  return response.data || 0;
}

export async function getCompaniesOptions(
  resale?: string,
  ctx = undefined
): Promise<ISelectOption[]> {
  const { data: response } = await setupApi(ctx).get<ISelectOption[]>(
    '/companies/options',
    {
      params: {
        resale,
      },
    }
  );

  return response;
}

export async function loadCompanyConsumptionById(
  id: string,
  filters = {} as ILoadCompanyConsumptionByIdFilters,
  ctx = undefined
): Promise<CompanyConsumptionDTO> {
  const params: Record<string, any> = {};

  if (filters.initialDate && filters.finalDate && filters.year) {
    params.initialDate = filters.initialDate;
    params.finalDate = filters.finalDate;
    params.year = filters.year;
  }

  const response = await setupApi(ctx).get<CompanyConsumptionDTO>(
    `/companies/${id}/consumption`,
    { params }
  );

  return response.data;
}

export async function exportCompanies(
  filters = {} as ILoadCompaniesFilters,
  ctx = undefined
): Promise<CompanyResponseDTO[]> {
  const params = formatCompaniesFiltersToParams(filters);

  const response = await setupApi(ctx).get('/companies/export', { params });

  return response.data;
}

export async function addActionToCompanies(
  ids: string[],
  data: AddActionRequestDTO,
  ctx = undefined
): Promise<any> {
  await Promise.all(
    ids.map(async (id) => {
      await setupApi(ctx).post(`/companies/${id}/actions`, {
        feedback: data.feedback,
        action: data.action._id,
      });
    })
  );
}

export async function loadCompanySubscriptionsById(
  id: string,
  ctx = undefined
): Promise<CompanySubscriptionsDTO> {
  const response = await setupApi(ctx).get(`/companies/${id}/subscription`);

  return response.data;
}

export async function createCompany(
  data: CompanyRequestDTO,
  ctx = undefined
): Promise<CompanyResponseDTO> {
  const response = await setupApi(ctx).post('/companies', data);
  return response.data;
}

export async function updateCompany(
  id: string,
  data: CompanyRequestDTO,
  ctx = undefined
): Promise<CompanyResponseDTO> {
  const response = await setupApi(ctx).put(`/companies/${id}`, data);
  return response.data;
}

export async function createCompanySubscription(
  id: string,
  data: CreateCompanySubscriptionRequestDTO,
  type: TSubscriberSubscriptionType,
  ctx = undefined
): Promise<void> {
  await setupApi(ctx).post(`/companies/${id}/${type}/subscription`, data);
}

export async function loadSubscriptionOccurrenceParameters(
  companyId: string,
  ctx = undefined
): Promise<ISubscriberSubscriptionOccurrenceParameter[]> {
  const response = await setupApi(ctx).get<
    ISubscriberSubscriptionOccurrenceParameter[]
  >(`/companies/${companyId}/subscription/occurrence-parameters`);

  return response.data;
}

export async function validateOriginCnpj(
  companyId: string,
  data: ValidateOriginCnpjRequestDTO,
  ctx = undefined
): Promise<ValidateOriginCnpjResponseDTO> {
  const response = await setupApi(ctx).post<ValidateOriginCnpjResponseDTO>(
    `/companies/${companyId}/subscription/validate-origin-cnpj`,
    data
  );

  return response.data;
}

export async function loadCnpjChangeOriginContract(
  companyId: string,
  document: string,
  endingDate?: string,
  ctx = undefined
): Promise<CnpjChangeOriginContractDTO> {
  const response = await setupApi(ctx).get<CnpjChangeOriginContractDTO>(
    `/companies/${companyId}/subscription/cnpj-change/origin-contract`,
    {
      params: {
        document,
        ...(endingDate && { endingDate }),
      },
    }
  );

  return response.data;
}

export async function downloadCompanyContractPdf(
  id: string,
  type: 'system' | 'rental'
): Promise<AxiosResponse<string> | undefined> {
  const response = await setupApi().get(
    `/companies/${id}/subscription/contract-pdf/download`,
    { responseType: 'blob', params: { type } }
  );
  return response.status === 200 ? response : undefined;
}

export async function downloadCompanySubscriptionFile(
  id: string,
  fileId: string
): Promise<AxiosResponse<string> | undefined> {
  const response = await setupApi().get(
    `/companies/${id}/subscription/files/${fileId}/download`,
    { responseType: 'blob' }
  );
  return response.status === 200 ? response : undefined;
}

export async function downloadCompanySubscriptionAmendmentFile(
  companyId: string,
  subscriptionId: string,
  amendmentId: string,
  fileId: string
): Promise<AxiosResponse<string> | undefined> {
  const response = await setupApi().get(
    `/companies/${companyId}/subscription/${subscriptionId}/amendments/${amendmentId}/files/${fileId}/download`,
    { responseType: 'blob' }
  );
  return response.status === 200 ? response : undefined;
}

export async function cancelCompanySubscriptionAmendment(
  companyId: string,
  subscriptionId: string,
  amendmentId: string,
  reason: string
): Promise<CompanySubscriptionAmendmentDTO> {
  const response = await setupApi().patch<CompanySubscriptionAmendmentDTO>(
    `/companies/${companyId}/subscription/${subscriptionId}/amendments/${amendmentId}/cancel`,
    { reason }
  );
  return response.data;
}

export async function createCompanySubscriptionAmendment(
  id: string,
  subscriptionId: string,
  data: CreateCompanySubscriptionAmendmentRequestDTO[],
  options: CreateCompanySubscriptionAmendmentOptions = {},
  ctx = undefined
): Promise<void> {
  await setupApi(ctx).post(
    `/companies/${id}/subscription/${subscriptionId}/amendments`,
    {
      items: data,
      ...options,
    }
  );
}

export async function validateCompanySubscriptionAmendment(
  id: string,
  subscriptionId: string,
  data: CreateCompanySubscriptionAmendmentRequestDTO[],
  options: Pick<
    CreateCompanySubscriptionAmendmentOptions,
    'automaticEvaluation'
  > = {},
  ctx = undefined
): Promise<CompanySubscriptionAmendmentValidationResponse> {
  const response = await setupApi(
    ctx
  ).post<CompanySubscriptionAmendmentValidationResponse>(
    `/companies/${id}/subscription/${subscriptionId}/amendments/validate`,
    { items: data, ...options }
  );

  return response.data;
}
